(() => {
  if (window.hokoCartAddonsLoaded) {
    window.hokoCartAddonsInit?.();
    return;
  }
  window.hokoCartAddonsLoaded = true;

  const instances = new Map();
  let cartState = null;
  let generation = 0;
  let refreshTimer;
  const cartPath = () => (window.Shopify?.routes?.root || '/') + 'cart';
  const optionFor = item => {
    const input = item.querySelector('.hoko-bundle__variant');
    return input?.tagName === 'SELECT' ? input.selectedOptions[0] : input;
  };
  const selectedItems = root => [...root.querySelectorAll('[data-bundle-item]')]
    .filter(item => item.querySelector('.hoko-bundle__check')?.checked);

  function activeInstances() {
    for (const [root] of instances) {
      if (!root.isConnected) instances.delete(root);
    }
    return instances.values();
  }

  function formatMoney(instance, cents) {
    const currency = cartState?.currency || instance.root.dataset.currency || 'EUR';
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency', currency
    }).format(cents / 100);
  }

  function renderCart(instance) {
    const { root, strings } = instance;
    const total = Math.max(0, Number(cartState?.total_price ?? root.dataset.cartTotal) || 0);
    const rate = total >= 100000 ? 10 : total >= 80000 ? 8 : total >= 50000 ? 5 : 0;
    const bonus = Math.round(total * rate / 100);
    root.dataset.cartTotal = total;
    root.dataset.cartRate = rate;
    root.querySelector('[data-addon-cart-total]').textContent = formatMoney(instance, total);
    root.querySelector('[data-addon-earned]').hidden = rate === 0;
    root.querySelector('[data-addon-rate]').textContent = rate + ' %';
    root.querySelector('[data-addon-bonus]').textContent = formatMoney(instance, bonus);
    root.querySelectorAll('[data-tier]').forEach(tier => {
      tier.classList.toggle('is-active', Number(tier.dataset.tier) * 100 <= total);
    });
    root.querySelector('[data-progress]').style.width = Math.min(100, total / 1000) + '%';
    root.querySelector('.hoko-bundle__progress').setAttribute('aria-valuenow', Math.min(1000, total / 100));
    const next = [50000, 80000, 100000].find(threshold => threshold > total);
    root.querySelector('[data-addon-next]').textContent = next
      ? strings.next.replace('[amount]', formatMoney(instance, next - total))
        .replace('[rate]', next === 50000 ? '5' : next === 80000 ? '8' : '10')
      : strings.highest;
  }

  function renderSelection(instance) {
    const { root, strings } = instance;
    const selected = selectedItems(root);
    let total = 0;
    root.querySelectorAll('[data-bundle-item]').forEach(item => {
      const chosen = selected.includes(item);
      item.classList.toggle('is-selected', chosen);
      if (chosen) total += Number(optionFor(item)?.dataset.price || 0);
    });
    root.querySelector('[data-addon-count]').textContent = selected.length;
    root.querySelector('[data-addon-selected-total]').textContent = formatMoney(instance, total);
    const button = root.querySelector('[data-addon-add]');
    const adding = root.dataset.adding === 'true';
    button.disabled = adding || selected.length === 0;
    button.textContent = adding ? strings.adding : selected.length
      ? strings.add + ' · ' + selected.length : strings.select;
  }

  function applyCart(cart) {
    if (!cart || !Number.isFinite(Number(cart.total_price))) return;
    generation += 1;
    cartState = cart;
    for (const instance of activeInstances()) renderCart(instance);
  }

  async function refreshCart() {
    const version = ++generation;
    const response = await fetch(cartPath() + '.js', {
      credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('Cart refresh failed');
    const cart = await response.json();
    if (version === generation) applyCart(cart);
    return cartState;
  }

  function scheduleRefresh() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => {
      if ([...activeInstances()].length) refreshCart().catch(() => {});
    }, 100);
  }

  function onCartEvent(event) {
    if (event.detail?.cart && Number.isFinite(Number(event.detail.cart.total_price))) {
      applyCart(event.detail.cart);
    } else {
      scheduleRefresh();
    }
  }

  function init(root) {
    if (instances.has(root)) return;
    let strings;
    try { strings = JSON.parse(root.querySelector('[data-addon-strings]').textContent); }
    catch (_) { return; }
    const instance = { root, strings };
    instances.set(root, instance);
    root.dataset.initialized = 'true';
    if (!cartState) {
      cartState = {
        total_price: Number(root.dataset.cartTotal) || 0,
        item_count: Number(root.dataset.cartCount) || 0,
        currency: root.dataset.currency
      };
    }
    renderCart(instance);
    renderSelection(instance);

    root.addEventListener('change', event => {
      root.querySelector('[data-addon-error]').hidden = true;
      root.querySelector('[data-addon-status]').hidden = true;
      if (event.target.matches('select.hoko-bundle__variant')) {
        const item = event.target.closest('[data-bundle-item]');
        const option = optionFor(item);
        const image = item.querySelector('.hoko-bundle__item-image img');
        if (image && option?.dataset.image) {
          image.src = option.dataset.image;
          image.removeAttribute('srcset');
        }
        item.querySelector('[data-item-price]').textContent = formatMoney(instance, Number(option?.dataset.price || 0));
      }
      renderSelection(instance);
    });

    root.querySelector('[data-addon-add]').addEventListener('click', async () => {
      if (root.dataset.adding === 'true') return;
      const selected = selectedItems(root);
      const error = root.querySelector('[data-addon-error]');
      const status = root.querySelector('[data-addon-status]');
      error.hidden = true;
      status.hidden = true;
      if (!selected.length) return;
      if (selected.some(item => optionFor(item)?.dataset.available !== 'true')) {
        error.textContent = strings.unavailable;
        error.hidden = false;
        return;
      }
      const items = selected.map(item => ({ id: Number(optionFor(item).value), quantity: 1 }));
      const button = root.querySelector('[data-addon-add]');
      root.dataset.adding = 'true';
      button.setAttribute('aria-busy', 'true');
      renderSelection(instance);
      try {
        const response = await fetch(cartPath() + '/add.js', {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items, sections: ['mini-cart'], sections_url: window.location.pathname })
        });
        const result = await response.json();
        if (!response.ok) throw new Error(typeof result.description === 'string' ? result.description : strings.error);
        const cart = await refreshCart().catch(() => null);
        selected.forEach(item => { item.querySelector('.hoko-bundle__check').checked = false; });
        status.textContent = strings.added;
        status.hidden = false;
        root.dispatchEvent(new CustomEvent('variant:added', {
          bubbles: true, detail: { variant: result.items?.[0] || result }
        }));
        if (cart) {
          document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
            bubbles: true, detail: { cart }
          }));
        }
        const drawerCart = cart ? { ...cart } : null;
        if (drawerCart && result.sections?.['mini-cart']) drawerCart.sections = result.sections;
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true, detail: { cart: drawerCart, openMiniCart: true }
        }));
        if (!cart) scheduleRefresh();
      } catch (failure) {
        error.textContent = failure.message || strings.error;
        error.hidden = false;
        scheduleRefresh();
      } finally {
        delete root.dataset.adding;
        button.removeAttribute('aria-busy');
        renderSelection(instance);
      }
    });
  }

  function initAll(scope = document) {
    scope.querySelectorAll('[data-hoko-cart-addons]').forEach(init);
    scheduleRefresh();
  }
  window.hokoCartAddonsInit = () => initAll();
  document.addEventListener('cart:updated', onCartEvent);
  document.addEventListener('cart:refresh', onCartEvent);
  document.addEventListener('shopify:section:load', event => initAll(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [root] of instances) if (event.target.contains(root)) instances.delete(root);
  });
  window.addEventListener('pageshow', scheduleRefresh);
  window.addEventListener('focus', scheduleRefresh);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') scheduleRefresh();
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();
})();
